import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

// Layout Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import LoadingScreen from "./components/LoadingScreen";
import NewsletterModal from "./components/NewsletterModal";

// Pages
import Home from "./pages/Home";
import Outrange from "./pages/Outrange";
import EastPortal from "./pages/EastPortal";
import WestPortal from "./pages/WestPortal";
import ArticlePage from "./pages/ArticlePage";
import StreamfinitePage from "./pages/StreamfinitePage";
import AdminDashboard from "./pages/AdminDashboard";
import AboutUs from "./pages/AboutUs";
import Careers from "./pages/Careers";
import PressKit from "./pages/PressKit";
import Contact from "./pages/Contact";
import RSSFeedPage from "./pages/RSSFeedPage";
import Community from "./pages/Community";
import Commission from "./pages/Commission";
import Docs from "./pages/Docs";
import CommunityCalendar from "./components/CommunityCalendar";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import DMCA from "./pages/DMCA";
import TermsOfService from "./pages/TermsOfService";

// Hooks
import useSupabaseAuth from "./hooks/useSupabaseAuth";

// Styles
import "./App.css";

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

// PageWrapper with fade/slide animation
const PageWrapper = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 20 }}
    transition={{ duration: 0.5 }}
    style={{ minHeight: "100vh" }}
  >
    {children}
  </motion.div>
);

// AnimatedRoutes now uses PageWrapper and includes ScrollToTop
const AnimatedRoutes = ({ setIsLoading }) => {
  const location = useLocation();

  useEffect(() => {
    setIsLoading(true);
    const timeout = setTimeout(() => setIsLoading(false), 500); // simulate page load delay
    return () => clearTimeout(timeout);
  }, [location.pathname, setIsLoading]);

  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
          <Route path="/outrange" element={<PageWrapper><Outrange /></PageWrapper>} />
          <Route path="/outrange/east" element={<PageWrapper><EastPortal /></PageWrapper>} />
          <Route path="/outrange/west" element={<PageWrapper><WestPortal /></PageWrapper>} />
          <Route path="/article/:slug" element={<PageWrapper><ArticlePage /></PageWrapper>} />
          <Route path="/streamfinite" element={<PageWrapper><StreamfinitePage /></PageWrapper>} />
          <Route path="/admin/*" element={<PageWrapper><AdminDashboard /></PageWrapper>} />
          <Route path="/about" element={<PageWrapper><AboutUs /></PageWrapper>} />
          <Route path="/careers" element={<PageWrapper><Careers /></PageWrapper>} />
          <Route path="/press-kit" element={<PageWrapper><PressKit /></PageWrapper>} />
          <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
          <Route path="/articles" element={<PageWrapper><RSSFeedPage /></PageWrapper>} />
          <Route path="/community" element={<PageWrapper><Community /></PageWrapper>} />
          <Route path="/community/calendar" element={<PageWrapper><CommunityCalendar /></PageWrapper>} />
          <Route path="/commission" element={<PageWrapper><Commission /></PageWrapper>} />
          <Route path="/docs" element={<PageWrapper><Docs /></PageWrapper>} />
          <Route path="/privacy" element={<PageWrapper><PrivacyPolicy /></PageWrapper>} />
          <Route path="/cookies" element={<PageWrapper><CookiePolicy /></PageWrapper>} />
          <Route path="/dmca" element={<PageWrapper><DMCA /></PageWrapper>} />
          <Route path="/terms" element={<PageWrapper><TermsOfService /></PageWrapper>} />
        </Routes>
      </AnimatePresence>
    </>
  );
};

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const [isLoading, setIsLoading] = useState(false);

  // NEW: Newsletter state
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  const openNewsletter = () => setNewsletterOpen(true);
  const closeNewsletter = () => setNewsletterOpen(false);

  useSupabaseAuth();

  useEffect(() => {
    console.log("[Auth] Supabase auth initialized");
  }, []);

  return (
    <div className="min-h-screen bg-netflix-black text-white relative">
      {!isAdminRoute && <Header openNewsletter={openNewsletter} />}
      {isLoading && <LoadingScreen />}

      {/* Newsletter modal mounted here */}
      <NewsletterModal isOpen={newsletterOpen} onClose={closeNewsletter} />

      <AnimatedRoutes setIsLoading={setIsLoading} />
      {!isAdminRoute && <Footer openNewsletter={openNewsletter} />}
    </div>
  );
};

function App() {
  return (
    <React.StrictMode>
      <Router>
        <AppContent />
      </Router>
    </React.StrictMode>
  );
}

export default App;
